import { useState } from "react";
import { ProjectList } from "../Constants/ProjectDetails";
import { motion, AnimatePresence } from "framer-motion";
import { AiOutlineGithub, AiOutlineClose } from "react-icons/ai";
import { TbWorldWww } from "react-icons/tb";

const Modal = ({ onClose, projectKey }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [singleProject] = ProjectList.filter((data) => data.key === projectKey);
  const { codeLink, demoLink, info, name, skills, youtube_url } = singleProject;

  const closeModal = () => {
    setIsOpen(false);
    document.body.classList.remove("overflow-hidden");
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm cursor-pointer"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl cursor-default rounded-2xl bg-white dark:bg-zinc-900 shadow-2xl border border-gray-100 dark:border-zinc-700"
          >
            {/* Orange top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-mainTheme to-fadeMainTheme rounded-t-2xl" />

            {/* Close button */}
            <motion.button
              onClick={closeModal}
              whileTap={{ scale: 0.9 }}
              className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-gray-400 hover:bg-mainTheme hover:text-white transition-colors duration-200"
            >
              <AiOutlineClose size={18} />
            </motion.button>

            <div className="p-6 flex flex-col gap-5">
              {/* Header */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white pr-8">
                  {name}
                </h2>
                <div className="mt-1 h-0.5 w-12 bg-mainTheme rounded-full" />
              </div>

              {/* Live Preview */}
              {demoLink && (
                <div className="relative w-full rounded-xl overflow-hidden bg-gray-100 dark:bg-zinc-800" style={{ height: "320px" }}>
                  <iframe
                    src={demoLink}
                    title={name}
                    style={{
                      width: "1280px",
                      height: "720px",
                      transform: "scale(0.5)",
                      transformOrigin: "top left",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      border: "none",
                      pointerEvents: "none",
                    }}
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin"
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-10 flex items-center justify-center gap-1.5 bg-black/40 backdrop-blur-sm text-white text-xs cursor-pointer hover:bg-mainTheme/80 transition-colors"
                    onClick={() => window.open(demoLink, "_blank", "noopener noreferrer")}
                  >
                    <TbWorldWww size={14} /> Open live site
                  </div>
                </div>
              )}

              {/* Description */}
              {info && (
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {info}
                </p>
              )}

              {/* Skills */}
              {skills && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-mainTheme mb-2">
                    Tech Stack
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skills[0].split(",").map((skill, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1 rounded-full bg-orange-50 dark:bg-zinc-800 text-mainTheme border border-orange-200 dark:border-zinc-600 font-medium"
                      >
                        {skill.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* YouTube embed */}
              {youtube_url && (
                <div className="rounded-xl overflow-hidden">
                  <iframe
                    src={youtube_url}
                    title={name}
                    className="w-full aspect-video rounded-xl"
                    allowFullScreen
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-1">
                {codeLink && (
                  <button
                    onClick={() => window.open(codeLink, "_blank", "noopener noreferrer")}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-mainTheme text-mainTheme font-semibold text-sm hover:bg-mainTheme hover:text-white transition-colors duration-200"
                  >
                    <AiOutlineGithub size={18} /> View Code
                  </button>
                )}
                {demoLink && (
                  <button
                    onClick={() => window.open(demoLink, "_blank", "noopener noreferrer")}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-mainTheme text-white font-semibold text-sm hover:bg-orange-700 transition-colors duration-200"
                  >
                    <TbWorldWww size={18} /> Live Demo
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;
